import { create } from 'zustand';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export const useCart = create((set, get) => ({
  cartItems: [],
  isLoading: false,
  error: null,
  lastValidationTime: null,
  validationResults: {},

  // === Backend Cart Management ===

  /**
   * Add ticket to cart with immediate backend reservation
   */
  addToCart: async (ticketItem) => {
    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to add items to cart');
      }

      // Call backend to reserve tickets immediately
      const response = await fetch(`${API_BASE_URL}/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ticketTypeId: ticketItem.ticketTypeId,
          quantity: ticketItem.quantity,
          selectedSeatIds: ticketItem.selectedSeatIds,
          holdToken: ticketItem.holdToken
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in to add items to cart.');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add tickets to cart');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to add tickets to cart');
      }

      // Refresh cart from backend to get updated state
      await get().loadCartFromBackend();

      console.log('Tickets reserved successfully:', result.data);

    } catch (error) {
      console.error('Failed to add to cart:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Remove specific seat from cart
   */
  removeSpecificSeat: async (eventId, seatId) => {
    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to manage cart');
      }

      const response = await fetch(`${API_BASE_URL}/cart/remove-seat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          eventId: eventId,
          seatId: seatId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove seat from cart');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to remove seat from cart');
      }

      // Refresh cart
      await get().loadCartFromBackend();

    } catch (error) {
      console.error('Failed to remove seat:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Remove ticket from cart and release backend reservation
   */
  removeFromCart: async (ticketTypeId, quantity = null) => {
    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to manage cart');
      }

      // Call backend to release ticket reservations
      const response = await fetch(`${API_BASE_URL}/cart/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ticketTypeId: ticketTypeId,
          quantity: quantity
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in to manage cart.');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove tickets from cart');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to remove tickets from cart');
      }

      console.log('Tickets released successfully:', result);
      console.log('Reloading cart from backend...');

      // Refresh cart from backend to get updated state
      await get().loadCartFromBackend();

      console.log('Cart reloaded. New cart items:', get().cartItems);

    } catch (error) {
      console.error('Failed to remove from cart:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Load cart items from backend reservations
   */
  loadCartFromBackend: async () => {
    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        set({ cartItems: [] });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/cart/reservations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // User not authenticated, clear cart
          set({ cartItems: [] });
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load cart');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to load cart');
      }

      // Convert backend reservations to frontend cart format
      const cartItems = (result.data || []).flatMap(reservation => {
        const isAssigned = reservation.seatAssignment === 'ASSIGNED';
        const quantity = reservation.quantity || 0;

        if (isAssigned && quantity > 0) {
          // Split assigned seats into individual items
          return Array.from({ length: quantity }).map((_, index) => ({
            id: `cart-${reservation.ticketTypeId}-${index}-${Date.now()}`,
            ticketTypeId: reservation.ticketTypeId,
            ticketTypeName: reservation.ticketTypeName,
            ticketCategory: reservation.ticketCategory,
            category: reservation.ticketCategory,
            seatAssignment: reservation.seatAssignment,
            seatLabels: [reservation.seatLabels?.[index]].filter(Boolean),
            seatId: reservation.seatIds?.[index], // Capture specific seat ID if available
            eventId: reservation.eventId,
            eventTitle: reservation.eventTitle,
            eventCategory: reservation.eventCategory,
            eventDate: reservation.eventStartTime,
            venueName: reservation.venueName,
            quantity: 1, // Individual item
            unitPrice: reservation.unitPrice,
            totalPrice: reservation.unitPrice, // Price for one
            currencyCode: reservation.currencyCode,
            addedAt: reservation.reservedAt,
            expiresAt: reservation.expiresAt,
            isReserved: true
          }));
        }

        // Return aggregated item for GA / Unseated
        return [{
          id: `cart-${reservation.ticketTypeId}`,
          ticketTypeId: reservation.ticketTypeId,
          ticketTypeName: reservation.ticketTypeName,
          ticketCategory: reservation.ticketCategory,
          category: reservation.ticketCategory,
          seatAssignment: reservation.seatAssignment,
          seatLabels: reservation.seatLabels || [],
          seatIds: reservation.seatIds || [],
          eventId: reservation.eventId,
          eventTitle: reservation.eventTitle,
          eventCategory: reservation.eventCategory,
          eventDate: reservation.eventStartTime,
          venueName: reservation.venueName,
          quantity: reservation.quantity,
          unitPrice: reservation.unitPrice,
          totalPrice: reservation.totalPrice,
          currencyCode: reservation.currencyCode,
          addedAt: reservation.reservedAt,
          expiresAt: reservation.expiresAt,
          isReserved: true
        }];
      });

      console.log('Loaded cart from backend:', cartItems.length, 'items');
      set({ cartItems });

    } catch (error) {
      console.error('Failed to load cart from backend:', error);
      set({ error: error.message, cartItems: [] });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Clear all cart items and release all reservations
   */
  clearCart: async () => {
    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        set({ cartItems: [] });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/cart/clear`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to clear cart');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to clear cart');
      }

      set({ cartItems: [], validationResults: {}, lastValidationTime: null });

    } catch (error) {
      console.error('Failed to clear cart:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // === Legacy Cart Validation (still needed for checkout) ===

  /**
 * Validate cart items against current availability
 */
  validateCartContents: async () => {
    const { cartItems } = get();

    if (cartItems.length === 0) {
      const emptyResult = { errors: [], totalAmount: 0, isValid: true };
      set({
        validationResults: emptyResult,
        lastValidationTime: new Date().toISOString()
      });
      return emptyResult;
    }

    set({ isLoading: true, error: null });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to validate cart');
      }

      // Prepare validation request
      const validationItems = cartItems.map(item => ({
        ticketTypeId: item.ticketTypeId,
        quantity: item.quantity
      }));

      // Call backend validation endpoint
      const response = await fetch(`${API_BASE_URL}/cart/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(validationItems),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Cart validation failed');
      }

      const results = await response.json();

      // Transform backend response to match expected format
      const transformedResults = {
        isValid: results.valid || false,
        errors: results.errors || [],
        totalAmount: results.totalAmount || 0,
        availabilities: results.availabilities || []
      };

      set({
        validationResults: transformedResults,
        lastValidationTime: new Date().toISOString(),
        isLoading: false
      });

      return transformedResults;

    } catch (error) {
      console.error('Cart validation failed:', error);
      const errorResult = {
        errors: [error.message],
        isValid: false,
        totalAmount: 0
      };
      set({
        error: error.message,
        isLoading: false,
        validationResults: errorResult
      });
      throw error;
    }
  },

  // === Computed Properties ===

  /**
   * Get total number of items in cart
   */
  getCartCount: () => {
    const { cartItems } = get();
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  },

  /**
   * Get total cart value
   */
  getCartTotal: () => {
    const { cartItems } = get();
    return cartItems.reduce((total, item) => total + item.totalPrice, 0);
  },

  /**
   * Check if cart has items
   */
  hasItems: () => {
    const { cartItems } = get();
    return cartItems.length > 0;
  },

  /**
   * Get cart item by ticket type ID
   */
  getCartItem: (ticketTypeId) => {
    const { cartItems } = get();
    return cartItems.find(item => item.ticketTypeId === ticketTypeId);
  },

  /**
   * Check if ticket type is in cart
   */
  isInCart: (ticketTypeId) => {
    const { cartItems } = get();
    return cartItems.some(item => item.ticketTypeId === ticketTypeId);
  },

  /**
   * Get quantity of specific ticket type in cart
   */
  getQuantityInCart: (ticketTypeId) => {
    const { cartItems } = get();
    return cartItems
      .filter(item => item.ticketTypeId === ticketTypeId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);
  },

  // === Cart State Management ===

  /**
   * Set error state
   */
  setError: (error) => {
    set({ error });
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Initialize cart on app start (load from backend if authenticated)
   */
  initializeCart: async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      await get().loadCartFromBackend();
    } else {
      set({ cartItems: [] });
    }
  },

  /**
   * Handle user logout (clear cart)
   */
  handleLogout: () => {
    set({
      cartItems: [],
      validationResults: {},
      lastValidationTime: null,
      error: null
    });
  },

  // === Legacy Methods (for backward compatibility) ===

  /**
   * Update quantity (redirects to backend operations)
   */
  updateQuantity: async (ticketTypeId, newQuantity) => {
    const currentQuantity = get().getQuantityInCart(ticketTypeId);

    if (newQuantity === 0) {
      await get().removeFromCart(ticketTypeId);
    } else if (newQuantity > currentQuantity) {
      // Add more tickets
      await get().addToCart({
        ticketTypeId: ticketTypeId,
        quantity: newQuantity - currentQuantity
      });
    } else if (newQuantity < currentQuantity) {
      // Remove some tickets
      await get().removeFromCart(ticketTypeId, currentQuantity - newQuantity);
    }
  },

  // === Error Recovery ===

  /**
   * Retry failed operation
   */
  retryLastOperation: async () => {
    // Implementation depends on tracking last operation
    // For now, just reload cart
    await get().loadCartFromBackend();
  }
}));

// Initialize cart when store is created
useCart.getState().initializeCart();
